/**
 * Unified read model: one row per event with separate flags for host / attendee / RSVP / saved.
 * Rows are produced from `GET /api/events/my` (see {@link apiMyRelatedDtoToUserEventListItem}).
 */

import { getLocalCalendarYmd } from "@/lib/events/event-archive"
import type { EventData, PersistedRsvpStatus } from "@/lib/events/types"
import { calendarYmdStartLocalMs } from "@/lib/events/event-datetime"

export type UserEventPrimaryRelationship =
  | "host"
  | "attendee"
  | "rsvp"
  | "saved"
  | "collaborator"

export type UserEventRelationFlags = {
  isHosted: boolean
  isAttending: boolean
  isSaved: boolean
  isRsvped: boolean
  rsvpStatus: PersistedRsvpStatus | null
  primaryRelationship: UserEventPrimaryRelationship
  /** Server-computed; same field as on {@link EventData.canUploadMedia}. */
  canUploadMedia?: boolean
}

export type UserEventListItem = EventData & UserEventRelationFlags

// TODO(recurrence-rollout): list surfaces are currently occurrence/event-row based; collapse to one dashboard card per series with a featured occurrence resolver.
export function computePrimaryRelationship(
  isHosted: boolean,
  isAttending: boolean,
  isRsvped: boolean,
  _isSaved: boolean,
  isWorkspaceCollaborator = false,
): UserEventPrimaryRelationship {
  if (isHosted) return "host"
  if (isWorkspaceCollaborator) return "collaborator"
  if (isAttending) return "attendee"
  if (isRsvped) return "rsvp"
  return "saved"
}

function rsvpDisplayLabel(
  status: PersistedRsvpStatus | null | undefined,
): string | null {
  if (status === "going") return "Going"
  if (status === "maybe") return "Maybe"
  if (status === "declined") return "Not going"
  return null
}

/** Subtle secondary line — host > attendee > RSVP > saved. */
export function formatUserEventRelationsLine(
  item: UserEventListItem,
  opts?: { omitRsvpLabel?: boolean },
): string {
  const parts: string[] = []
  if (item.isHosted) parts.push("Hosting")
  if (item.primaryRelationship === "collaborator") parts.push("Shared account")
  if (item.isAttending) parts.push("Attending")
  if (!opts?.omitRsvpLabel) {
    const rsvp = rsvpDisplayLabel(item.rsvpStatus)
    if (rsvp) parts.push(rsvp)
  }
  if (item.isSaved) parts.push("Saved")
  return parts.join(" · ")
}

export function sortUserEventListItems(
  items: UserEventListItem[],
): UserEventListItem[] {
  const t0 = calendarYmdStartLocalMs(getLocalCalendarYmd())
  if (t0 == null) {
    return [...items].sort((a, b) => a.date.localeCompare(b.date))
  }

  return [...items].sort((a, b) => {
    const da = calendarYmdStartLocalMs(a.date)
    const db = calendarYmdStartLocalMs(b.date)
    if (da == null || db == null) return a.date.localeCompare(b.date)
    const aUp = da >= t0
    const bUp = db >= t0
    if (aUp !== bUp) return aUp ? -1 : 1
    if (aUp) return da - db
    return db - da
  })
}

/** Guest-facing “Attending” surface: not host-only; saved, participant, and/or RSVP. */
export function selectAttendingSurfaceEvents(
  items: UserEventListItem[],
): UserEventListItem[] {
  return items.filter(
    (i) =>
      !i.isHosted && (i.isAttending || i.isSaved || i.isRsvped),
  )
}

/** Past published events the user relates to (archive / timeline). */
export function selectArchiveSurfaceEvents(
  items: UserEventListItem[],
  isEventPast: (e: EventData) => boolean,
): UserEventListItem[] {
  return items.filter(
    (i) =>
      i.isPublished &&
      isEventPast(i) &&
      (i.isHosted || i.isSaved || i.isAttending || i.isRsvped),
  )
}

/** Hosting dashboard: events this user hosts. */
export function selectHostedSurfaceEvents(
  items: UserEventListItem[],
): UserEventListItem[] {
  return items.filter((i) => i.isHosted)
}
